/**
 * Puschies Suchseite (sections/p-main-search.liquid) — Hyper-Verhalten ohne AJAX:
 * - <p-search>: Sortierung (Neuladen mit sort_by), Raster/Liste (localStorage „p-search-layout“),
 *   Seitenleiste ein-/ausblenden (ab 1280 px), Filter-Formulare ohne leere Felder abschicken,
 *   Seitenleiste schickt Änderungen sofort ab (Hyper filtert ebenfalls sofort).
 * - <p-search-drawer>: Filter-Drawer von links, beim Öffnen an <body> portiert (Theme-Section-Wrapper
 *   fangen position:fixed), Overlay/ESC/X schließen, Fokus zurück auf den Auslöser.
 * - <p-price-range>: Doppelschieberegler ↔ Von/Bis-Felder, Füllbalken über --p-range-min/max.
 */
const LAYOUT_KEY = 'p-search-layout';

const readLayout = () => {
  try {
    return localStorage.getItem(LAYOUT_KEY);
  } catch (e) {
    return null;
  }
};
const writeLayout = (value) => {
  try {
    localStorage.setItem(LAYOUT_KEY, value);
  } catch (e) {
    /* privater Modus: Ansicht wird nicht gemerkt */
  }
};

const submitClean = (form) => {
  const params = new URLSearchParams();
  new FormData(form).forEach((value, key) => {
    if (value === '' || value == null) return;
    params.append(key, value);
  });
  window.location.href = `${form.getAttribute('action')}?${params.toString()}`;
};

if (!customElements.get('p-price-range')) {
  class PPriceRange extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 0;
      this.sliderMin = this.querySelector('.p-price__slider--min');
      this.sliderMax = this.querySelector('.p-price__slider--max');
      this.inputMin = this.querySelector('.p-price__input--min');
      this.inputMax = this.querySelector('.p-price__input--max');
      this.range = this.querySelector('.p-price__range');
      if (!this.sliderMin || !this.sliderMax) return;
      this.sliderMin.addEventListener('input', () => this.fromSlider('min'));
      this.sliderMax.addEventListener('input', () => this.fromSlider('max'));
      this.sliderMin.addEventListener('change', () => this.commit());
      this.sliderMax.addEventListener('change', () => this.commit());
      this.inputMin?.addEventListener('change', () => this.fromInput());
      this.inputMax?.addEventListener('change', () => this.fromInput());
      this.paint();
    }

    fromSlider(which) {
      let min = Number(this.sliderMin.value);
      let max = Number(this.sliderMax.value);
      if (min > max) {
        if (which === 'min') min = max;
        else max = min;
        this.sliderMin.value = min;
        this.sliderMax.value = max;
      }
      this.inputMin.value = min > 0 ? min : '';
      this.inputMax.value = max < this.max ? max : '';
      this.paint();
    }

    fromInput() {
      let min = this.inputMin.value === '' ? 0 : Math.max(0, Number(this.inputMin.value));
      let max = this.inputMax.value === '' ? this.max : Math.min(this.max, Number(this.inputMax.value));
      if (min > max) [min, max] = [max, min];
      this.sliderMin.value = min;
      this.sliderMax.value = max;
      this.paint();
    }

    commit() {
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }

    paint() {
      if (!this.max || !this.range) return;
      this.range.style.setProperty('--p-range-min', `${(Number(this.sliderMin.value) / this.max) * 100}%`);
      this.range.style.setProperty('--p-range-max', `${(Number(this.sliderMax.value) / this.max) * 100}%`);
    }
  }
  customElements.define('p-price-range', PPriceRange);
}

if (!customElements.get('p-search-drawer')) {
  class PSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-p-drawer-close]')) this.close();
      });
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close();
      };
    }

    open(trigger) {
      this.trigger = trigger;
      if (this.parentElement !== document.body) document.body.appendChild(this);
      this.hidden = false;
      document.documentElement.classList.add('p-scroll-lock');
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.querySelector('.p-sdrawer__close')?.focus({ preventScroll: true });
        });
      });
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.classList.remove('p-scroll-lock');
      const inner = this.querySelector('.p-sdrawer__inner');
      const done = () => {
        this.hidden = true;
        this.trigger?.focus({ preventScroll: true });
      };
      const duration = parseFloat(getComputedStyle(inner).transitionDuration) || 0;
      if (duration) inner.addEventListener('transitionend', done, { once: true });
      else done();
    }
  }
  customElements.define('p-search-drawer', PSearchDrawer);
}

if (!customElements.get('p-search')) {
  class PSearch extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('p-search-drawer');
      this.grid = this.querySelector('[data-p-grid]');
      this.sidebar = this.querySelector('.p-search__sidebar');

      this.querySelectorAll('[data-p-drawer-open]').forEach((button) =>
        button.addEventListener('click', () => this.drawer?.open(button))
      );

      this.querySelector('[data-p-sidebar-toggle]')?.addEventListener('click', (event) => {
        if (!this.sidebar) return;
        this.sidebar.hidden = !this.sidebar.hidden;
        event.currentTarget.setAttribute('aria-expanded', String(!this.sidebar.hidden));
      });

      this.querySelector('[data-p-sort]')?.addEventListener('change', (event) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', event.target.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });

      const forms = [...this.querySelectorAll('[data-p-facets-form]')];
      if (this.drawer) forms.push(...this.drawer.querySelectorAll('[data-p-facets-form]'));
      forms.forEach((form) => {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          submitClean(form);
        });
        if (form.hasAttribute('data-auto-submit')) {
          form.addEventListener('change', (event) => {
            if (event.target.matches('.p-price__slider')) return;
            submitClean(form);
          });
        }
      });

      this.layoutButtons = [...this.querySelectorAll('[data-p-layout]')];
      this.layoutButtons.forEach((button) =>
        button.addEventListener('click', () => {
          this.setLayout(button.dataset.pLayout);
          writeLayout(button.dataset.pLayout);
        })
      );
      if (readLayout() === 'list') this.setLayout('list');
    }

    setLayout(mode) {
      if (!this.grid) return;
      this.grid.classList.toggle('p-search__grid--list', mode === 'list');
      this.layoutButtons.forEach((button) => {
        const active = button.dataset.pLayout === mode;
        button.classList.toggle('p-tbtn--active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
  }
  customElements.define('p-search', PSearch);
}
